import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Validators, FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { CitadelNotificationService, CitadelErrorLoggingService } from '@citadel/core';
import { AuthenticationType } from 'src/app/models/authentication-type.model';
import { EmailValidationResult } from 'src/app/models/email-validation-result.model';
import { RegistrationService } from 'src/app/services/registration-service.service';
import { SignInService } from 'src/app/services/sign-in-service';
import { logFormErrors, logWhenInvalid } from '../form-logging';

@Component({
  selector: 'app-sign-in-email-step',
  templateUrl: './sign-in-email-step.component.html',
  styleUrls: ['./sign-in-email-step.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInEmailStepComponent {
  private http = inject(HttpClient);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private notify = inject(CitadelNotificationService);
  private errorLogging = inject(CitadelErrorLoggingService);
  private signInService = inject(SignInService);
  private registration = inject(RegistrationService);

  private defaultErrorMessage: string = 'An error occurred. Please try again';
  private isConfirming: boolean = false;

  protected emailForm: FormGroup = this.fb.group({
    email: [this.signInService.email(), [Validators.required, Validators.email]],
  });
  protected emailFormSubmitted: boolean = false;

  // convenience getter for easy access to form fields
  protected get usernameForm() {
    return this.emailForm.controls;
  }

  constructor() {
    logWhenInvalid(this.errorLogging, this.emailForm, 'email');
  }

  protected goToPassword() {
    this.emailFormSubmitted = true;
    // stop here if form is invalid
    if (this.emailForm.invalid) {
      logFormErrors(this.errorLogging, this.emailForm, 'email');
      this.notify.error('Please enter your email address', '');
      return;
    }
    this.signInService.email.set(this.emailForm.controls.email.value);
    this.confirmEmail();
  }

  private confirmEmail() {
    const email = this.signInService.email();
    if (!email.match(/^([a-zA-Z0-9_\-\.]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/)) {
      return;
    }
    // A double click would otherwise check the email twice and, for EmailOtp, send two OTPs.
    if (this.isConfirming) {
      return;
    }
    this.isConfirming = true;
    this.http.get<EmailValidationResult>(`/api/auth/${email}`).subscribe({
      next: account => {
        this.signInService.account.set(account);
        this.signInService.otpSent.set(false);

        if (account.authenticationTypeL === AuthenticationType.Email) {
          this.isConfirming = false;
          this.router.navigate(['/sign-in', 'password']);
        } else if (account.authenticationTypeL === AuthenticationType.EmailOtp) {
          this.sendOtpEmail(account.requestID);
        } else { // AD Account
          if (localStorage) {
            localStorage.setItem('Citadel.Auth.Service.AccountType', 'CitadelActiveDirectory');
          }
          window.location.href = `/external/challenge?userName=${account.userName}&impersonate=${this.signInService.impersonate()}&returnUrl=${encodeURI(this.signInService.returnUrl())}`;
        }
      }, error: _error => {
        this.isConfirming = false;
        if (_error.error.message) {
          this.notify.error(_error.error.message, "");
        } else {
          this.notify.error(_error.message, "");
        }
      }
    });
  }

  /** EmailOtp accounts have no password: mail the OTP and go straight to OTP entry. */
  private sendOtpEmail(requestId: string) {
    this.registration.sendOtpEmail(requestId).subscribe({
      next: _ => {
        this.isConfirming = false;
        this.notify.success('OTP sent', "");
        this.signInService.otpType.set('Email');
        this.signInService.otpSent.set(true);
        this.router.navigate(['/sign-in', 'otp']);
      }, error: _ => {
        this.isConfirming = false;
        this.notify.error(this.defaultErrorMessage, "");
      }
    });
  }

  protected updateIdentification(e: Event, id: string) {
    if (id === 'email') {
      (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.replace(/ /g, '');
    }
  }
}
